import React, { useState } from "react";
import { ClipboardIcon } from "@heroicons/react/24/outline";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { PageHeader } from "../../components/layout/header/Header";
import {
  DashboardCompletionStepBox,
  ExpandablePanel,
  Modal,
  ToolTip,
  VerticalProgressBarCirclesWithText,
} from "@truevault/aquilo";
import { useOrganization, usePrimaryOrganizationId } from "../../hooks";
import { usePrivacyCenter } from "../../hooks/usePrivacyCenter";
import { Routes } from "../../root/routes";
import { useSelfOrganizationUser } from "../../hooks/useOrganization";
import { UserRole } from "../../../common/service/server/Types";
import {
  isDeveloperInstructionsOnboardingFullyComplete,
  TruevaultOnboardingProgress,
  TruevaultPrivacyCenterOnboardingProgress,
  useOnboardingProgress,
} from "../../../common/hooks/useOnboardingProgress";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import ConfettiComponent from "../../../common/components/Confetti";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "./PrivacyNoticeChecklistItems";
import { DevInstructionsDSARFormsOnboardingSlug } from "./privacy-center/DSARForms";
import { PageLoading } from "../../components";

export const isWebsiteConsentEnabled = (
  privacyCenterOnboardingProgress?: TruevaultPrivacyCenterOnboardingProgress,
) => {
  if (!privacyCenterOnboardingProgress) {
    return false;
  }
  let {
    isFinishedWithPoliciesNotices,
    isWebsiteConsentSetup,
    developerInstructionsOnboardingProgress,
  } = privacyCenterOnboardingProgress;
  // Only require the survey questions from the dev instructions to be complete, since the consent management vendors from dev instructions can become incomplete when a new vendor is added (and would then block existing orgs/users from accessing website consent after a new vendor is added)
  const devInstructionsSurveyComplete =
    developerInstructionsOnboardingProgress?.isFinishedDevInstructionsSurvey ?? false;
  return (isFinishedWithPoliciesNotices && devInstructionsSurveyComplete) || isWebsiteConsentSetup;
};

export const isPrivacyCenterDeveloperInstructionsEnabled = (
  privacyCenterOnboardingProgress?: TruevaultPrivacyCenterOnboardingProgress,
  isDSARFormsOnboardingComplete?: boolean,
) => {
  if (!privacyCenterOnboardingProgress) {
    return false;
  }

  const isDevInstructionsSurveyComplete =
    privacyCenterOnboardingProgress.developerInstructionsOnboardingProgress
      ?.isFinishedDevInstructionsSurvey ?? false;

  return isDSARFormsOnboardingComplete || isDevInstructionsSurveyComplete;
};

export const isDSARFormsEnabled = (
  privacyCenterOnboardingProgress?: TruevaultPrivacyCenterOnboardingProgress,
  isDSARFormsOnboardingComplete?: boolean,
) => {
  if (!privacyCenterOnboardingProgress) {
    return false;
  }
  let { isFinishedWithPoliciesNotices } = privacyCenterOnboardingProgress;
  return isFinishedWithPoliciesNotices || isDSARFormsOnboardingComplete;
};

export const PrivacyCenterDashboard = () => {
  const organizationId = usePrimaryOrganizationId();
  const [org] = useOrganization(organizationId);
  const currentOrgUser = useSelfOrganizationUser();
  const [privacyCenter, ,] = usePrivacyCenter(organizationId);

  const [hasSeenConfettiModal, setHasSeenConfettiModal] = useLocalStorage(
    "hasSeenPrivacyCenterDashboardConfettiModal",
    "false",
  );

  const [showConfettiModal, setShowConfettiModal] = useState(() => {
    let queryParams = new URLSearchParams(window.location.search);
    return queryParams.get("confetti") === "1";
  });

  const [onboardingProgress] = useOnboardingProgress(organizationId);
  const {
    isFinishedWithPoliciesNotices = false,
    isWebsiteConsentSetup = false,
    developerInstructionsOnboardingProgress = {
      isConsentManagementDevInstructionsComplete: false,
      isFinishedDevInstructionsSurvey: false,
    },
  } = onboardingProgress?.privacyCenterOnboardingProgress || {};
  const isDevInstructionsStepFullyComplete = isDeveloperInstructionsOnboardingFullyComplete(
    developerInstructionsOnboardingProgress,
  );
  const { isFinishedDevInstructionsSurvey } = developerInstructionsOnboardingProgress;

  const { answers: devInstructionsAnswers } = useSurvey(
    organizationId,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );
  const isDSARFormsOnboardingComplete =
    devInstructionsAnswers &&
    devInstructionsAnswers[DevInstructionsDSARFormsOnboardingSlug] === "true";

  if (!org) {
    return <PageLoading />;
  }

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="Privacy Center" marginBottomPx={24} />

      {showConfettiModal && hasSeenConfettiModal === "false" && (
        <>
          <ConfettiModal
            closeModal={() => {
              setShowConfettiModal(false);
              setHasSeenConfettiModal("true");
            }}
          />
          <ConfettiComponent />
        </>
      )}

      <div className="tw-boundary">
        {privacyCenter && (
          <div className="mb-[48px]">
            <div>
              <span className="font-bold text-neutral-700">Privacy Center:</span>
              &nbsp;
              <span>{privacyCenter.url}</span>
            </div>
            <div>
              <span className="font-bold text-neutral-700">Business Account:</span>
              &nbsp;
              <span>
                {org.name} · {privacyCenter.requestEmail}
              </span>
            </div>
          </div>
        )}
        {(!isFinishedWithPoliciesNotices ||
          !isWebsiteConsentSetup ||
          !isDevInstructionsStepFullyComplete) &&
          // Always hide onboarding panel after org has completed onboarding latch (POL-7563)
          !org.finishedOnboardingLatch && (
            <ExpandablePanel title="Finish Privacy Center Set Up" titleFontSize="2xl">
              <>
                <p className="font-normal text-[18px] text-neutral-800 mt-6">
                  Complete the steps below to finish and publish your Privacy Center.
                </p>
                <div className="mt-6">
                  <VerticalProgressBarCirclesWithText
                    steps={[
                      {
                        name: "Policies and Notices",
                        description: "Set up your company's data policies",
                        status: isFinishedWithPoliciesNotices ? "complete" : "current",
                        route:
                          currentOrgUser?.role === "ADMIN"
                            ? Routes.privacyCenter.privacyPolicy
                            : null,
                      },
                      {
                        name: "Developer Instructions",
                        description: "See instructions for launching your privacy center",
                        status: isDevInstructionsStepFullyComplete
                          ? "complete"
                          : isFinishedWithPoliciesNotices
                          ? "current"
                          : "upcoming",
                        route: Routes.privacyCenter.publish,
                      },
                      {
                        name: "Website Consent",
                        description: "Configure your website's consent banner",
                        status: isWebsiteConsentSetup
                          ? "complete"
                          : isFinishedWithPoliciesNotices && isFinishedDevInstructionsSurvey
                          ? "current"
                          : "upcoming",
                        route: Routes.privacyCenter.consentBanner,
                      },
                    ]}
                  />
                </div>
              </>
            </ExpandablePanel>
          )}

        <PrivacyCenterStepGrid
          onboardingProgress={onboardingProgress}
          currentUserRole={currentOrgUser?.role}
          isDSARFormsOnboardingComplete={isDSARFormsOnboardingComplete}
          // Force unlock of all steps after org has completed onboarding latch (POL-7563)
          forceUnlocked={org?.finishedOnboardingLatch === true}
        />
      </div>
    </StandardPageWrapper>
  );
};

function PrivacyCenterStepGrid({
  onboardingProgress,
  currentUserRole,
  isDSARFormsOnboardingComplete,
  forceUnlocked,
}: {
  onboardingProgress?: TruevaultOnboardingProgress;
  currentUserRole: UserRole;
  isDSARFormsOnboardingComplete: boolean;
  forceUnlocked: boolean;
}) {
  let {
    isFinishedWithPoliciesNotices = false,
    isWebsiteConsentSetup = false,
    developerInstructionsOnboardingProgress = {
      isConsentManagementDevInstructionsComplete: false,
      isFinishedDevInstructionsSurvey: false,
    },
  } = onboardingProgress?.privacyCenterOnboardingProgress || {};
  const isDevInstructionsTileComplete = isDeveloperInstructionsOnboardingFullyComplete(
    developerInstructionsOnboardingProgress,
  );

  let policiesAndNoticesItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/retention-policy-icon.svg"
      title="Policies & Notices"
      description="Review & edit your privacy policy"
      status={
        currentUserRole === "GENERAL"
          ? "disabled"
          : isFinishedWithPoliciesNotices
          ? "complete"
          : "finish-set-up"
      }
    />
  );

  let websiteConsentItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/consent-banner-icon.svg"
      title="Website Consent"
      description="Configure your website's consent banner"
      status={isWebsiteConsentSetup ? "complete" : "finish-set-up"}
      isDimmed={
        !forceUnlocked &&
        !isWebsiteConsentEnabled(onboardingProgress?.privacyCenterOnboardingProgress)
      }
    />
  );

  let devInstructionsItem = (
    <DashboardCompletionStepBox
      iconFilename="/assets/images/stay_compliant/privacy_center/icons/developer-instructions-icon.svg"
      title="Developer Instructions"
      description="View steps for publishing your Privacy Center"
      status={isDevInstructionsTileComplete ? "complete" : "finish-set-up"}
      isDimmed={
        !forceUnlocked &&
        !isPrivacyCenterDeveloperInstructionsEnabled(
          onboardingProgress?.privacyCenterOnboardingProgress,
          isDSARFormsOnboardingComplete,
        )
      }
    />
  );

  let dsarFormsItem = (
    <DashboardCompletionStepBox
      icon={
        <div
          className="flex items-center justify-center"
          style={{
            background: "#DFD8F8",
            width: "48px",
            height: "48px",
            borderRadius: "8px",
            padding: "12px",
          }}
        >
          <ClipboardIcon className="text-neutral-700" style={{ width: "24px", height: "24px" }} />
        </div>
      }
      title="Request Forms"
      description="Customize your privacy request forms"
      status={isDSARFormsOnboardingComplete ? "complete" : "finish-set-up"}
      isDimmed={
        !forceUnlocked &&
        !isDSARFormsEnabled(
          onboardingProgress?.privacyCenterOnboardingProgress,
          isDSARFormsOnboardingComplete,
        )
      }
    />
  );

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "16px",
        maxWidth: "830px",
        marginTop: "48px",
      }}
    >
      {currentUserRole !== "GENERAL" ? (
        <a href={Routes.privacyCenter.privacyPolicy}>{policiesAndNoticesItem}</a>
      ) : (
        <ToolTip text={"Admin permissions required."} width="190px">
          {policiesAndNoticesItem}
        </ToolTip>
      )}
      {forceUnlocked ||
      isDSARFormsEnabled(
        onboardingProgress?.privacyCenterOnboardingProgress,
        isDSARFormsOnboardingComplete,
      ) ? (
        <a href={Routes.privacyCenter.dsarForms}>{dsarFormsItem}</a>
      ) : (
        dsarFormsItem
      )}
      {forceUnlocked ||
      isPrivacyCenterDeveloperInstructionsEnabled(
        onboardingProgress?.privacyCenterOnboardingProgress,
        isDSARFormsOnboardingComplete,
      ) ? (
        <a href={Routes.privacyCenter.publish}>{devInstructionsItem}</a>
      ) : (
        devInstructionsItem
      )}
      {forceUnlocked ||
      isWebsiteConsentEnabled(onboardingProgress?.privacyCenterOnboardingProgress) ? (
        <a href={Routes.privacyCenter.consentBanner}>{websiteConsentItem}</a>
      ) : (
        websiteConsentItem
      )}
    </div>
  );
}

function ConfettiModal({ closeModal }: { closeModal: () => void }) {
  return (
    <Modal
      onClose={closeModal}
      showOverlay
      closeOnOverlayClick
      closeButton
      contentPadding="p-[12px]"
    >
      <div
        className="flex items-center"
        // hack: use an inline style instead of the "flex-col" tailwind style because we already use the CSS class name "flex-col" in flex.scss, which causes other styles to be applied here
        style={{ flexDirection: "column" }}
      >
        <div>
          <svg
            width="48"
            height="48"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="48" height="48" rx="24" fill="#E2FDF3" />
            <path
              d="M17 25L21 29L31 19"
              stroke="#14A36B"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className="mt-[20px] text-gray-900 text-[18px] font-medium">
          Hooray! Your Site is looking great!
        </div>
        <div className="mt-[8px] text-gray-500 text-[14px] font-normal">
          <p>
            To further enrich your consumer's privacy choices, enable a consent banner on your site.
          </p>
        </div>
        <div className="mt-[8px]">
          <img
            src="/assets/images/stay_compliant/privacy_center/privacy-center-dashboard-confetti-modal.svg"
            className="w-[473px] h-[291px]"
            alt="Hooray"
          />
        </div>
      </div>
    </Modal>
  );
}
